import { Badge, Banner, Notice } from '@didto/ui';
import type { ReactNode } from 'react';
import { useSyncStatus } from '@/lib/network';
import type { SyncStatus } from '@/lib/network';

/**
 * OFFLINE or CONNECTION LOST across the top of every screen while the server cannot be reached
 * (ADR-037). What is on screen is the last that was loaded; nothing that needs the server now can be
 * started until the connection is back, and it comes back on its own.
 */
export function ConnectionBanner({ children }: { children?: ReactNode }) {
  const sync = useSyncStatus();
  if (sync.online) return null;
  return (
    <Banner
      title={sync.connectivity === 'offline' ? 'Offline' : 'Connection lost'}
      testId="connection-banner"
      state={sync.connectivity}
    >
      {children ?? (
        <>
          Showing what was last loaded. New orders, payments and changes are paused until the
          connection is back{sync.pending ? ` · ${sync.pending} waiting to send` : ''}.
        </>
      )}
    </Banner>
  );
}

/** The connection in a word, for the navigation and the sign-in screens. */
export function SyncPill({ state, pending }: Pick<SyncStatus, 'state' | 'pending'>) {
  if (state === 'offline')
    return (
      <Badge tone="danger" dot testId="sync-pill">
        OFFLINE{pending ? ` · ${pending} waiting` : ''}
      </Badge>
    );
  if (state === 'lost')
    return (
      <Badge tone="danger" dot testId="sync-pill">
        CONNECTION LOST{pending ? ` · ${pending} waiting` : ''}
      </Badge>
    );
  if (state === 'syncing')
    return (
      <Badge tone="warning" dot testId="sync-pill">
        SYNCING · {pending}
      </Badge>
    );
  return (
    <Badge tone="success" dot testId="sync-pill">
      SYNCED
    </Badge>
  );
}

/** The same news inside an open sheet (the banner sits behind it): why its action is paused. */
export function SheetConnectionNotice() {
  const sync = useSyncStatus();
  if (sync.online) return null;
  return (
    <Notice tone="danger" role="status" testId="sheet-connection">
      {sync.connectivity === 'offline' ? 'Offline' : 'Connection lost'}: anything that needs the
      server is paused until the connection is back.
    </Notice>
  );
}
